import { Call, Device } from "@twilio/voice-sdk";
import { useEffect, useRef, useState } from "react";
import { PatientControllerApi } from "../services";
import { getConfigObject, getXtenantId } from "../utils/utils";
import { useSnackbar } from "./useSnackbar";

type CallStatus =
  | "connected"
  | "disconnected"
  | "ringing"
  | "calling"
  | "ended"
  | "connecting"
  | "error";

/**
 * Converts phone number to E.164 format
 * @param phone - Phone number in various formats (e.g., "(123) 456-7890" or "+11234567890")
 * @returns Phone number in E.164 format with +1 prefix
 */
const convertPhoneNumber = (phone: string): string =>
  "+1" + phone?.replace(/\D/g, "");

/**
 * Fetches Twilio access token from the backend API
 * @returns Promise resolving to Twilio access token
 * @throws Error if token is not available
 */
const fetchTwilioToken = async (patientId: string) => {
  const token = await new PatientControllerApi(getConfigObject())
    .getTwilioToken(patientId, undefined, getXtenantId())
    .then((res: any) => {
      console.log("val here check token -->", res);
      return res?.data?.data?.token;
    });

  console.log("val here inti", token);
  return token;
};

/**
 * Custom hook for managing Twilio voice calls
 * Handles device initialization, call connection, and cleanup
 *
 * @returns Object containing call status, device, call instance, and control functions
 *
 * @author [Rajkumar Rathod]
 */
const useTwilioCallController = (patientId: string) => {
  const device = useRef<Device | null>(null);
  const call = useRef<Call | null>(null);
  const isInitializedTrack = useRef(false);
  const [callStatus, setCallStatus] = useState<CallStatus>("disconnected");
  const [initializationError, setInitializationError] = useState<string | null>(
    null
  );
  const showSnackbar = useSnackbar();

  const showSnackMessage = (message: string) => {
    showSnackbar({
      message: message,
      type: "error",
    });
  };

  /**
   * Adds event listeners to Twilio device instance
   * @param deviceInstance - Twilio Device instance
   */
  const addDeviceListeners = (deviceInstance: Device) => {
    console.log("Call ended (reject/finish) 1");
    deviceInstance.on("registered", () => console.log("Twilio.Device Ready!"));

    deviceInstance.on("cancel", () => console.log("Twilio.Device Ready!"));
    deviceInstance.on("disconnect", () => setCallStatus("disconnected"));
    deviceInstance.on("cancel", () => setCallStatus("disconnected"));

    deviceInstance.on("disconnect", () => {
      console.log("Call ended (reject/finish)");
      setCallStatus("disconnected");
    });
    deviceInstance.on("error", (error: Error) => {
      console.error("Twilio.Device Error:", error.message);
      setInitializationError(error.message);
      setCallStatus("error");
    });
    deviceInstance.on("unregistered", () => {
      setCallStatus("disconnected");
      console.log("Twilio.Device unregistered");
    });
  };

  /**
   * Initializes Twilio device with access token
   * Cleans up existing device if present before initializing new one
   * @param token - Twilio access token
   */
  const initializeDevice = async (
    token: string,
    connectToPhoneNumber?: () => void
  ) => {
    try {
      // Clean up existing device if any
      if (device.current) {
        device.current.unregister();
        device.current.destroy();
        device.current = null;
      }

      const newDevice = new Device(token, {
        logLevel: 1,
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
      });

      device.current = newDevice;
      addDeviceListeners(newDevice);
      await newDevice.register();
      isInitializedTrack.current = true;
      setInitializationError(null);
      if (connectToPhoneNumber) connectToPhoneNumber();
    } catch (error) {
      console.log("val here check error -->", error);
      setCallStatus("error");
      showSnackMessage("Error initializing Twilio device");
      isInitializedTrack.current = false;
    }
  };

  /**
   * Initiates a call to the specified phone number
   * Validates phone number and sets up call event listeners
   *
   * @warn no need to convert or add +1
   * @param phoneNumber - Phone number to call (various formats accepted)
   */
  const connectToPhoneNumber = async (phoneNumber: string) => {
    if (!device.current || !isInitializedTrack.current) {
      console.error("Device not initialized");
      setCallStatus("error");
      return;
    }

    // Remove non-digit characters for validation
    const digitsOnly = phoneNumber.replace(/\D/g, "");
    if (!phoneNumber || digitsOnly.length < 10) {
      showSnackMessage("Invalid phone number");
      return;
    }

    console.log("val here check digit", digitsOnly);

    setCallStatus("connecting");
    const params = { To: convertPhoneNumber(phoneNumber) };

    try {
      const newCall = await device.current.connect({ params });

      const endCall = (status: CallStatus) => {
        setCallStatus(status);
        try {
          newCall.disconnect();
        } catch {
          // call already torn down
        }
        call.current = null;
      };

      newCall.on("accept", () => setCallStatus("connected"));
      newCall.on("disconnect", () => endCall("disconnected"));
      newCall.on("cancel", () => endCall("disconnected"));
      // Callee declined: Twilio fires `reject` on the call instance.
      newCall.on("reject", () => endCall("ended"));
      newCall.on("error", (err: any) => {
        console.error("Twilio Call Error:", err?.code, err?.message);
        endCall("error");
      });

      call.current = newCall;
    } catch (error) {
      console.log("val here check error -->", error);
      setCallStatus("error");
      showSnackMessage("Call error");
    }
  };

  /**
   * Quick connect to a phone number
   * Fetches Twilio token, initializes device, and connects to phone number
   *
   * @see WhenToUse - When You want to connect to a phone number quickly without overwhelming the device with too many calls
   *
   * @param contactNumber - Phone number to connect to
   */
  const quickConnect = async (contactNumber: string) => {
    const token = await fetchTwilioToken(patientId);
    console.log("val here check token 222", token);

    if (!!token)
      await initializeDevice(token, () => connectToPhoneNumber(contactNumber));
  };

  /**
   * Disconnects the current active call
   */
  const disconnectCall = () => {
    setCallStatus("disconnected");
    if (call.current) {
      call.current.disconnect();
      call.current = null;
    }
  };

  // Initialize device on mount
  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      try {
        console.log("val here init 1");

        // useRunOnce(async () => {
        const token = await fetchTwilioToken(patientId);
        // });
        if (isMounted && !!token) await initializeDevice(token);
      } catch (error) {
        if (isMounted) {
          showSnackMessage("Error fetching Twilio token");
        }
      }
    };

    init();

    // Cleanup on unmount
    return () => {
      isMounted = false;
      call.current?.disconnect();
      if (device.current) {
        device.current.unregister();
        device.current.destroy();
        device.current = null;
      }
    };
  }, []);

  return {
    callStatus,
    setCallStatus,
    device,
    call,
    initializeDevice,
    connectToPhoneNumber,
    quickConnect,
    disconnectCall,
    initializationError,
  };
};

export default useTwilioCallController;

// const useRunOnce = (fn: () => void) => {
//   const [isRun, setIsRun] = useState(false);
//   if (!isRun) {
//     fn();
//     setIsRun(true);
//   }
// };
